import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from '@environments/environment';
import { HTTP_INVALID_PERMISSIONS_ERROR } from '@static/constants';
import { HttpService } from '@static/services/http.service';
import { SnackService } from '@static/services/snack.service';
import { catchError, finalize, of, timeout } from 'rxjs';

export interface IProjectUploadListState {
  id: string;
  name: string;
  state: string;
  percent: number;
  stage: number;
};

export enum EProjectUploadState {
  INITIALISING = 0,
  PROCESSING = 1,
  COMPLETE = 2,
  ERROR = 3,
}

@Component({
  selector: 'app-upload-list',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatIconModule,
    DecimalPipe,
  ],
  templateUrl: './upload-list.component.html',
  styleUrl: './upload-list.component.scss',
})
export class UploadListComponent implements AfterViewInit, OnDestroy {
  public dialogRef: MatDialogRef<UploadListComponent> = inject(MatDialogRef<UploadListComponent>);
  private _httpService: HttpService = inject(HttpService);
  private _snackService: SnackService = inject(SnackService);

  @ViewChild(MatSort) sort!: MatSort;

  protected loading: boolean;
  protected uploads: MatTableDataSource<IProjectUploadListState, MatPaginator>;
  private _timer?: number;

  protected dispCols = ["name", "state", "percent", "stage", "controls"];

  constructor() {
    this.loading = true;
    this.uploads = new MatTableDataSource();

    // Set recurring query
    this._timer = window.setInterval(() => {
      this._checkUploadState();
    }, 2000);
    this._checkUploadState();
  }

  /**
   * Init the table datasource sorting
   */
  ngAfterViewInit(): void {
    this.uploads.sort = this.sort;
  }

  /**
   * Get the current state of all project uploads
   */
  private _checkUploadState(): void {
    this._httpService.get<IProjectUploadListState[]>(`${environment.backendUri}/upload/list`)
      .pipe(
        // Ignore timeouts
        timeout(1000),
        catchError(() => {
          return of(null);
        }),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (res: (IProjectUploadListState[] | null)) => {
          if (res === null) return;
          this.uploads.data = res;
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
            this._snackService.openErrorSnack('Insufficient permissions for this project', err);
          }
          else this._snackService.openErrorSnack('Failed to get project upload state', err);
        },
      });
  }

  /**
   * Cancel a project upload
   *
   * @param upload  upload to cancel
   */
  protected stopUpload(upload: IProjectUploadListState): void {
    this._httpService.getP(`${environment.backendUri}/project/${upload.id}/upload/cancel`)
      .then()
      .catch((err: HttpErrorResponse) => {
        if (err.status === HTTP_INVALID_PERMISSIONS_ERROR) {
          this._snackService.openErrorSnack('Insufficient permissions for this project', err);
        }
        else this._snackService.openErrorSnack('Failed to cancel project upload', err);
      });
  }

  /**
   * Stop the timer on destroy
   */
  ngOnDestroy(): void {
    if (this._timer) window.clearInterval(this._timer);
  }

  /**
   * Close the dialog
   */
  protected close(): void {
    this.dialogRef.close();
  }
}
